import { lazy, type LazyExoticComponent, type ComponentType } from "react";

export type Audience = "platform" | "tenant";

export interface UiAccess {
  role?: string;
  isGlobalOperator?: boolean;
  isPlatformMember?: boolean;
  isPlatformOwner?: boolean;
  isPlatformSupport?: boolean;
  canSeeProspects?: boolean;
  canSeeSetup?: boolean;
  canSeeOperations?: boolean;
  canSeeReporting?: boolean;
  canSeeAdmin?: boolean;
  canViewSystemConsole?: boolean;
  canManagePlatformAdmins?: boolean;
  canSuspendTenant?: boolean;
  canEnterViewAs?: boolean;
  viewAs?: {
    active: boolean;
    impersonatedAccountId: string | null;
    ownerUserId: string;
  };
}

export interface RouteSpec {
  /** Path relative to the module entry (or absolute when start with `/`). */
  path: string;
  /** Index route inside the module (renders at the module root). */
  index?: boolean;
  /** Render as <Navigate to=...> instead of a component. */
  redirectTo?: string;
  /** Lazy-loaded page component. Required unless redirectTo is set. */
  component?: LazyExoticComponent<ComponentType<unknown>>;
}

export interface NavModule {
  key: string;
  label: string;
  /** Entry path used by the sidebar (NavLink target). */
  path: string;
  icon: string;
  audience: Audience;
  /** Returns true when the module should appear for the given uiAccess. */
  visibleWhen: (uiAccess: UiAccess | null | undefined) => boolean;
  /** Routes registered inside this module (declared relative to `path`). */
  routes: RouteSpec[];
}

const lazyDefault = <T extends ComponentType<unknown>>(
  importer: () => Promise<Record<string, T>>,
  named: string,
): LazyExoticComponent<T> =>
  lazy(() => importer().then((m) => ({ default: m[named]! })));

// ─── Tenant modules — what HVAC operators / owners see ────────────────

const WorkspacePage = lazyDefault(
  () => import("@/pages/operations/WorkspacePage"),
  "WorkspacePage",
);
const RecordsPage = lazyDefault(
  () => import("@/pages/RecordsPage"),
  "RecordsPage",
);
const RecordDetailPage = lazyDefault(
  () => import("@/pages/records/RecordDetailPage"),
  "RecordDetailPage",
);
const CalendarPage = lazyDefault(
  () => import("@/pages/CalendarPage"),
  "CalendarPage",
);
const ReportingPage = lazyDefault(
  () => import("@/pages/reporting/ReportingPage"),
  "ReportingPage",
);
const RecoveryPage = lazyDefault(
  () => import("@/pages/reporting/RecoveryPage"),
  "RecoveryPage",
);
const DeliveriesPage = lazyDefault(
  () => import("@/pages/reporting/DeliveriesPage"),
  "DeliveriesPage",
);
const BillingPage = lazyDefault(
  () => import("@/pages/BillingPage"),
  "BillingPage",
);
const PlatformPage = lazyDefault(
  () => import("@/pages/platform/PlatformPage"),
  "PlatformPage",
);
const PlatformSetupPage = lazyDefault(
  () => import("@/pages/platform/SetupPage"),
  "SetupPage",
);
const ImportsPage = lazyDefault(
  () => import("@/pages/platform/ImportsPage"),
  "ImportsPage",
);
const NotificationsPage = lazyDefault(
  () => import("@/pages/platform/NotificationsPage"),
  "NotificationsPage",
);
const PlatformAdminPage = lazyDefault(
  () => import("@/pages/platform/AdminPage"),
  "AdminPage",
);
const MembersPage = lazyDefault(
  () => import("@/pages/platform/MembersPage"),
  "MembersPage",
);
const MessagingCompliancePage = lazyDefault(
  () => import("@/pages/platform/MessagingCompliancePage"),
  "MessagingCompliancePage",
);

// ─── Platform-owner modules — what Dorsey-Worx sees on /system ────────

const SystemOverviewPage = lazyDefault(
  () => import("@/pages/system/SystemOverviewPage"),
  "SystemOverviewPage",
);
const TenantsPage = lazyDefault(
  () => import("@/pages/system/TenantsPage"),
  "TenantsPage",
);
const PlatformAuditPage = lazyDefault(
  () => import("@/pages/system/PlatformAuditPage"),
  "PlatformAuditPage",
);
const QueueHealthPage = lazyDefault(
  () => import("@/pages/system/QueueHealthPage"),
  "QueueHealthPage",
);
const PlatformMembersPage = lazyDefault(
  () => import("@/pages/system/PlatformMembersPage"),
  "PlatformMembersPage",
);
const ObservabilityPage = lazyDefault(
  () => import("@/pages/system/ObservabilityPage"),
  "ObservabilityPage",
);
const SystemSettingsPage = lazyDefault(
  () => import("@/pages/system/SystemSettingsPage"),
  "SystemSettingsPage",
);
const SystemBillingPage = lazyDefault(
  () => import("@/pages/system/BillingPage"),
  "BillingPage",
);
const SystemSignupsPage = lazyDefault(
  () => import("@/pages/system/SignupsPage"),
  "SignupsPage",
);
const SystemDispatchPage = lazyDefault(
  () => import("@/pages/system/DispatchPage"),
  "DispatchPage",
);

// Default-safe predicate so omitting `visibleWhen` lets the module show
// to anyone who can reach the parent shell.
const always = () => true;

export const TENANT_MODULES: NavModule[] = [
  {
    key: "operations",
    label: "Operations",
    path: "/operations",
    icon: "⚙",
    audience: "tenant",
    visibleWhen: (a) => Boolean(a?.canSeeOperations ?? true),
    routes: [
      { index: true, path: "", component: WorkspacePage },
      { path: "*", redirectTo: "/operations" },
    ],
  },
  {
    key: "records",
    label: "Records",
    path: "/records",
    icon: "📁",
    audience: "tenant",
    visibleWhen: always,
    routes: [
      { index: true, path: "", component: RecordsPage },
      { path: ":id", component: RecordDetailPage },
    ],
  },
  {
    key: "calendar",
    label: "Calendar",
    path: "/calendar",
    icon: "📅",
    audience: "tenant",
    visibleWhen: always,
    routes: [{ index: true, path: "", component: CalendarPage }],
  },
  {
    key: "reporting",
    label: "Reporting",
    path: "/reporting",
    icon: "📊",
    audience: "tenant",
    visibleWhen: (a) => Boolean(a?.canSeeReporting ?? true),
    routes: [
      { index: true, path: "", component: ReportingPage },
      { path: "recovery", component: RecoveryPage },
      { path: "deliveries", component: DeliveriesPage },
      { path: "*", redirectTo: "/reporting/recovery" },
    ],
  },
  {
    key: "billing",
    label: "Billing",
    path: "/billing",
    icon: "💳",
    audience: "tenant",
    visibleWhen: always,
    routes: [{ index: true, path: "", component: BillingPage }],
  },
  {
    key: "platform",
    label: "Workspace",
    path: "/platform",
    icon: "🛠",
    audience: "tenant",
    visibleWhen: always,
    routes: [
      { index: true, path: "", component: PlatformPage },
      { path: "setup", component: PlatformSetupPage },
      { path: "imports", component: ImportsPage },
      { path: "notifications", component: NotificationsPage },
      { path: "messaging-compliance", component: MessagingCompliancePage },
      { path: "admin", component: PlatformAdminPage },
      { path: "members", component: MembersPage },
    ],
  },
];

export const PLATFORM_MODULES: NavModule[] = [
  {
    key: "system-overview",
    label: "Overview",
    path: "/system/overview",
    icon: "🌐",
    audience: "platform",
    visibleWhen: (a) => Boolean(a?.canViewSystemConsole),
    routes: [{ index: true, path: "", component: SystemOverviewPage }],
  },
  {
    key: "system-tenants",
    label: "Tenants",
    path: "/system/tenants",
    icon: "🏢",
    audience: "platform",
    visibleWhen: (a) => Boolean(a?.canViewSystemConsole),
    routes: [{ index: true, path: "", component: TenantsPage }],
  },
  {
    key: "system-billing",
    label: "Billing",
    path: "/system/billing",
    icon: "💳",
    audience: "platform",
    visibleWhen: (a) => Boolean(a?.canViewSystemConsole),
    routes: [{ index: true, path: "", component: SystemBillingPage }],
  },
  {
    key: "system-signups",
    label: "Signups",
    path: "/system/signups",
    icon: "🆕",
    audience: "platform",
    visibleWhen: (a) => Boolean(a?.canViewSystemConsole),
    routes: [{ index: true, path: "", component: SystemSignupsPage }],
  },
  {
    key: "system-dispatch",
    label: "Dispatch",
    path: "/system/dispatch",
    icon: "🛰",
    audience: "platform",
    visibleWhen: (a) => Boolean(a?.canViewSystemConsole),
    routes: [{ index: true, path: "", component: SystemDispatchPage }],
  },
  {
    key: "system-audit",
    label: "Audit",
    path: "/system/audit",
    icon: "📜",
    audience: "platform",
    visibleWhen: (a) => Boolean(a?.canViewSystemConsole),
    routes: [{ index: true, path: "", component: PlatformAuditPage }],
  },
  {
    key: "system-queue",
    label: "Queue Health",
    path: "/system/queue",
    icon: "📡",
    audience: "platform",
    visibleWhen: (a) => Boolean(a?.canViewSystemConsole),
    routes: [{ index: true, path: "", component: QueueHealthPage }],
  },
  {
    key: "system-members",
    label: "Platform Members",
    path: "/system/members",
    icon: "👥",
    audience: "platform",
    visibleWhen: (a) => Boolean(a?.canViewSystemConsole),
    routes: [{ index: true, path: "", component: PlatformMembersPage }],
  },
  {
    key: "system-observability",
    label: "Observability",
    path: "/system/observability",
    icon: "📈",
    audience: "platform",
    visibleWhen: (a) => Boolean(a?.canViewSystemConsole),
    routes: [{ index: true, path: "", component: ObservabilityPage }],
  },
  {
    key: "system-settings",
    label: "My Profile",
    path: "/system/settings",
    icon: "⚙",
    audience: "platform",
    visibleWhen: (a) => Boolean(a?.canViewSystemConsole),
    routes: [{ index: true, path: "", component: SystemSettingsPage }],
  },
];

export function visibleModules(
  audience: Audience,
  uiAccess: UiAccess | null | undefined,
): NavModule[] {
  const source = audience === "platform" ? PLATFORM_MODULES : TENANT_MODULES;
  return source.filter((m) => m.visibleWhen(uiAccess));
}
